import { Popover, Transition } from '@headlessui/react';
import { DownArrowIcon } from 'assets/icons';
import { FC, useCallback, useEffect, useRef, useState } from 'react';
import { Calendar } from 'react-date-range';
import DateUtils from 'utils/dateUtils';
import classNames from 'classnames';
import { createPortal } from 'react-dom';
import './DayPicker.css';

export interface PredefinedOption {
  label: string;
  date: Date | null;
  isSelected?: boolean;
  closesOnSelect?: boolean; // If false, selecting this option won't close the popover
  isCustom?: boolean; // Flag for custom options
  [key: string]: unknown; // Allow additional properties for custom matching logic
}

interface DayPickerProps {
  className?: string;
  showPastDates?: boolean;
  onSelect: (value: Date) => void;
  date: Date;
  prefixIcon?: React.ReactNode;
  labelClassName?: string;
  disabled?: boolean;
  timeZone?: string;
  disableBeforeDate?: Date | string;
  showDaysDifference?: boolean;
  customTrigger?: React.ReactNode;
  usePortal?: boolean;
  predefinedOptions?: PredefinedOption[];
  onPredefinedOptionSelect?: (option: PredefinedOption) => void;
  disableCalendar?: boolean; // New prop to disable only the calendar part
  optionMatcher?: (option: PredefinedOption, selectedDate: Date) => boolean; // Custom matching function
  position?: 'left' | 'right'; // Controls whether calendar opens to the left or right
  popoverPanelStyle?: React.CSSProperties;
}

const DayPicker: FC<DayPickerProps> = ({
  showPastDates = false,
  onSelect,
  date = new Date(),
  prefixIcon,
  labelClassName,
  disabled = false,
  timeZone = '',
  disableBeforeDate = null,
  showDaysDifference = false,
  customTrigger,
  usePortal = false,
  predefinedOptions = [],
  onPredefinedOptionSelect,
  disableCalendar = false,
  optionMatcher,
  position = 'left',
  popoverPanelStyle = {}
}) => {
  const [selectedDate, setSelectedDate] = useState<Date | null>(date ?? null);
  const [selectedPredefinedOption, setSelectedPredefinedOption] = useState<PredefinedOption | null>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const calendarRef = useRef<HTMLDivElement>(null);
  const [buttonRect, setButtonRect] = useState<DOMRect | null>(null);
  const [manualOpen, setManualOpen] = useState(false);
  const closePopoverRef = useRef<(() => void) | null>(null);

  // Default date matching function
  const defaultOptionMatcher = useCallback((option: PredefinedOption, currentDate: Date): boolean => {
    if (!option.date || !currentDate) return false;
    return DateUtils.areSameDates(option.date, currentDate);
  }, []);

  // Find which predefined option should be highlighted based on current date
  useEffect(() => {
    if (!predefinedOptions.length || !selectedDate) return;

    // Always find option that matches the current date
    const matcher = optionMatcher || defaultOptionMatcher;
    const matchedOption = predefinedOptions.find(option => matcher(option, selectedDate));

    if (matchedOption) {
      setSelectedPredefinedOption(matchedOption);
    } else {
      // If no option matches the date, fallback to custom option
      const customOption = predefinedOptions.find(option => option.isCustom === true);
      setSelectedPredefinedOption(customOption || null);
    }
  }, [selectedDate, predefinedOptions, optionMatcher]);

  // Update selected date when prop changes
  useEffect(() => {
    setSelectedDate(date);
  }, [date]);

  const updateButtonRect = useCallback(() => {
    if (buttonRef.current && usePortal) {
      setButtonRect(buttonRef.current.getBoundingClientRect());
    }
  }, [usePortal]);

  useEffect(() => {
    if (manualOpen && usePortal && buttonRef.current) {
      updateButtonRect();
    }
  }, [manualOpen, usePortal, updateButtonRect]);

  useEffect(() => {
    if (!usePortal) return;

    const handlePositionUpdate = () => {
      updateButtonRect();
    };

    window.addEventListener('resize', handlePositionUpdate);
    window.addEventListener('scroll', handlePositionUpdate, true);

    return () => {
      window.removeEventListener('resize', handlePositionUpdate);
      window.removeEventListener('scroll', handlePositionUpdate, true);
    };
  }, [usePortal, updateButtonRect]);

  // Handle outside clicks for portal mode only
  useEffect(() => {
    if (!usePortal || !manualOpen) return;

    const handleOutsideClick = (event: MouseEvent) => {
      const target = event.target as Element;

      // Don't close if clicking on the trigger button
      if (buttonRef.current?.contains(target)) {
        return;
      }

      // Don't close if clicking inside the portaled calendar container
      if (calendarRef.current?.contains(target)) {
        return;
      }

      // Close the popover for clicks outside the calendar (portal mode only)
      setManualOpen(false);
    };

    // Use capture phase to intercept before other handlers
    document.addEventListener('mousedown', handleOutsideClick, true);

    return () => {
      document.removeEventListener('mousedown', handleOutsideClick, true);
    };
  }, [manualOpen]);

  const handleSelect = useCallback(
    (value: Date) => {
      setSelectedDate(value);
      onSelect(value);

      // Close manual open state for portal mode
      if (usePortal) {
        setManualOpen(false);
      }
    },
    [onSelect, usePortal]
  );

  const handlePredefinedOptionClick = useCallback(
    (option: PredefinedOption) => {
      if (option.date) {
        // For regular options, update the date
        setSelectedDate(option.date);
        onSelect(option.date);
      }
      setSelectedPredefinedOption(option);
      onPredefinedOptionSelect?.(option);

      // Close manual open state for portal mode (based on closesOnSelect property)
      if (usePortal && option.closesOnSelect !== false) {
        setManualOpen(false);
      }
    },
    [onSelect, onPredefinedOptionSelect, usePortal]
  );

  const getCalendarPosition = () => {
    if (!buttonRect || !usePortal) return {};

    const baseStyle = {
      position: 'fixed' as const,
      top: buttonRect.bottom,
      minWidth: buttonRect.width,
      zIndex: 1000
    };

    if (position === 'right') {
      return {
        ...baseStyle,
        right: window.innerWidth - buttonRect.right
      };
    }

    return {
      ...baseStyle,
      left: buttonRect.left
    };
  };

  const renderCalendar = (close?: () => void) => {
    const handleCalendarChange = (value: Date | undefined) => {
      // The Calendar component from react-date-range passes a Date directly
      if (value instanceof Date) {
        handleSelect(value);
        // Only call close for non-portal mode since portal mode handles it in handleSelect
        if (!usePortal) {
          close?.();
        }
      }
    };

    const calendarComponent = (
      <Calendar
        date={selectedDate}
        onChange={handleCalendarChange}
        {...(!showPastDates && { minDate: disableBeforeDate ? new Date(disableBeforeDate) : new Date() })}
      />
    );

    // If no predefined options, render calendar as before
    if (!predefinedOptions.length) {
      return (
        <div
          style={{
            opacity: disableCalendar ? 0.5 : 1,
            pointerEvents: disableCalendar ? 'none' : 'auto'
          }}>
          {calendarComponent}
        </div>
      );
    }
    // Render calendar with predefined options on the left side
    return (
      <div className="parent-calender-with-predefined-options">
        {/* Predefined Options List - Vertical on left side */}
        <div className="predefined-option-button-list">
          <div className="flex flex-col">
            {predefinedOptions.map((option, index) => (
              <button
                key={index}
                onClick={() => {
                  handlePredefinedOptionClick(option);
                  // Only call close for non-portal mode since portal mode handles it in handlePredefinedOptionClick
                  if (!usePortal && option.closesOnSelect !== false) {
                    close?.();
                  }
                }}
                className={classNames(
                  'predefined-option-button',
                  selectedPredefinedOption?.label === option.label
                    ? 'predefined-option-button--selected'
                    : 'predefined-option-button--default'
                )}>
                {option.label}
              </button>
            ))}
          </div>
        </div>

        {/* Calendar */}
        <div
          className="w-full"
          style={{
            flex: 1,
            minWidth: 0
          }}>
          <div
            className="calendar-container w-full"
            style={{
              opacity: disableCalendar ? 0.5 : 1,
              pointerEvents: disableCalendar ? 'none' : 'auto'
            }}>
            {calendarComponent}
          </div>
        </div>
      </div>
    );
  };

  const renderPortalCalendar = (close: () => void) => {
    if (!usePortal || !buttonRect) return null;

    const portalRoot = document.getElementById('portal-root') || document.body;
    return createPortal(
      <div
        ref={calendarRef}
        className="fixed z-[1000] rounded-md"
        style={getCalendarPosition()}
        data-day-picker-portal="true"
        id="day-picker-portal"
        onMouseDown={e => e.stopPropagation()}
        onClick={e => e.stopPropagation()}>
        {renderCalendar(close)}
      </div>,
      portalRoot
    );
  };

  // Handle button click for portal mode
  const handleButtonClick = () => {
    if (usePortal) {
      setManualOpen(!manualOpen);
      if (!manualOpen && buttonRef.current) {
        updateButtonRect();
      }
    }
  };

  return (
    <Popover className="relative" {...(usePortal && { static: true })}>
      {({ open, close }) => {
        // Store close function for outside click handler
        closePopoverRef.current = close;

        const isCalendarOpen = usePortal ? manualOpen : open;

        return (
          <>
            <Popover.Button
              ref={buttonRef}
              className="w-full"
              disabled={disabled}
              {...(usePortal && { onClick: handleButtonClick })}>
              {customTrigger || (
                <div className="flex w-full items-center justify-between rounded bg-secondaryBtn py-3.5 px-5 text-17px">
                  <div className={classNames('flex items-center text-primaryText', labelClassName)}>
                    {prefixIcon && <div className="mr-2">{prefixIcon}</div>}
                    {DateUtils.getDateInFormat({
                      date: new Date(selectedDate),
                      dateFormat: 'MMMM dd, yyyy'
                    })}
                    {timeZone && <span className="ml-1 text-sbase text-primaryText">({timeZone})</span>}
                    {showDaysDifference && (
                      <span className="ml-1 text-accentText">({DateUtils.getDaysDifference(selectedDate)})</span>
                    )}
                  </div>
                  <DownArrowIcon />
                </div>
              )}
            </Popover.Button>

            <Transition
              show={isCalendarOpen}
              enter="transition duration-100 ease-out"
              enterFrom="transform scale-95 opacity-0"
              enterTo="transform scale-100 opacity-100"
              leave="transition duration-75 ease-out"
              leaveFrom="transform scale-100 opacity-100"
              leaveTo="transform scale-95 opacity-0">
              {usePortal ? (
                renderPortalCalendar(close)
              ) : (
                <Popover.Panel
                  className={classNames(
                    'absolute -top-2.5 z-50 w-full rounded-md',
                    position === 'right' ? 'right-0' : 'left-0'
                  )}
                  style={popoverPanelStyle}>
                  {renderCalendar(close)}
                </Popover.Panel>
              )}
            </Transition>
          </>
        );
      }}
    </Popover>
  );
};

export default DayPicker;
